import React from 'react'
import type { PaymentMethod } from '@mobi/api-types'
import {
  MasterCardButton,
  VisaButton,
} from '@mobi/component-library/Areas/Deposit/Buttons/BankCard'
import { BankCardButtonProps } from '@mobi/component-library/Areas/Deposit/Buttons/types'
import { Button } from '@mobi/component-library/Base/Buttons'
import { useBankCardDeposit, useBraintreeClient } from '../../../Hooks'
import { customerMustUseThreeDSecure } from '../../../Utils/helpers'
import { DepositError } from '../../DepositError'
import type { PanelProps } from '../../types'

type Props = PanelProps & {
  creditCard: PaymentMethod
  /** True to show issuer on button, false to show generic 'Deposit' button */
  showIssuer: boolean
}

export const BankCardButton = ({
  initialData,
  accountNumber,
  depositAmount,
  isDepositAllowed,
  creditCard,
  showIssuer,
  onStart,
  onCancel,
  onDepositing,
  onSuccess,
  onFailure,
}: Props) => {
  const mustUse3DSecure = customerMustUseThreeDSecure(initialData, depositAmount)

  const { clientTokenThreeDSecure, clientToken } = initialData

  const { isReady, client: braintreeClient } = useBraintreeClient(
    mustUse3DSecure ? clientTokenThreeDSecure : clientToken
  )

  const { deposit } = useBankCardDeposit({
    initialData,
    accountNumber,
    braintreeClient,
    mustUse3DSecure,
    onDepositing,
  })

  const onBankCardButtonClicked = async () => {
    // If depositing is not allowed, we stop here. While it isn't possible for
    // someone to get here while depositing isn't allowed, a user could try
    // to deposit by removing the `disabled` attribute through devtools.
    if (!isDepositAllowed) {
      return
    }

    onStart()

    const canDeposit = await isReady()

    if (!canDeposit) {
      onCancel?.()
      return
    }

    try {
      const response = await deposit({
        creditCard,
        isUsingNewCard: false,
        depositAmount,
      })

      const { isSuccess, ...errorDetails } = response

      if (isSuccess) {
        await onSuccess(depositAmount)
      } else {
        onFailure(DepositError.fromErrorDetails(errorDetails))
      }
    } catch (error) {
      if (error instanceof DepositError && error.reason === 'user_canceled') {
        onCancel?.()
        return
      }

      onFailure(DepositError.coerce(error, null))
    }
  }

  if (showIssuer) {
    const BankCardButton = determineBankCardButton(creditCard.issuerName)
    return (
      <BankCardButton
        lastFour={creditCard.lastFour}
        disabled={!isDepositAllowed}
        onClick={onBankCardButtonClicked}
      />
    )
  }

  return (
    <Button
      color='positive'
      size='md'
      disabled={!isDepositAllowed}
      onClick={onBankCardButtonClicked}
    >
      Deposit
    </Button>
  )
}

function determineBankCardButton(issuerName: string): React.FC<BankCardButtonProps> {
  switch (issuerName) {
    case 'MASTER_CARD':
      return MasterCardButton
    case 'VISA':
      return VisaButton
    default:
      return function () {
        return <></>
      }
  }
}
